import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  Alert,
  Linking,
  Platform,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { PlanService, CheckInService } from '../../services/ApiService';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

const DietPlanScreen = () => {
  const insets = useSafeAreaInsets();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [selectedDay, setSelectedDay] = useState(0);
  const [loading, setLoading] = useState(true);

  // Google Sheets URL - will be set from API response
  const [googleSheetsUrl, setGoogleSheetsUrl] = useState(null);

  // Latest admin notes from checkin history
  const [latestAdminNotes, setLatestAdminNotes] = useState(null);

  useEffect(() => {
    loadDietPlan();
  }, []);

  const loadDietPlan = async () => {
    try {
      const [plan, checkinHistory] = await Promise.all([
        PlanService.getCurrentPlan(),
        CheckInService.getCheckInHistory(1), // Get first page of checkin history
      ]);

      setCurrentPlan(plan);

      // Set Google Sheets URL from plan data
      if (plan?.googleSheetUrl) {
        setGoogleSheetsUrl(plan.googleSheetUrl);
      } else if (plan?.googleSheetId) {
        // Fallback: construct URL from ID if URL is not provided
        setGoogleSheetsUrl(`https://docs.google.com/spreadsheets/d/${plan.googleSheetId}`);
      }

      // Find latest admin notes from checkin history
      if (checkinHistory?.checkIns && checkinHistory.checkIns.length > 0) {
        const latestCheckinWithAdminNotes = checkinHistory.checkIns.find(checkin => checkin.adminNotes);
        if (latestCheckinWithAdminNotes) {
          setLatestAdminNotes(latestCheckinWithAdminNotes.adminNotes);
        }
      }

      // Set initial day to today
      const currentDayIndex = getCurrentDayIndex();
      setSelectedDay(currentDayIndex);
    } catch (error) {
      console.error('Diet plan error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDayName = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  };

  const getCurrentDayIndex = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days.indexOf(getDayName());
  };

  const getMealTypeIcon = (mealType) => {
    switch (mealType) {
      case 'breakfast': return 'sunny-outline';
      case 'lunch': return 'restaurant-outline';
      case 'dinner': return 'moon-outline';
      case 'snack': return 'cafe-outline';
      default: return 'restaurant-outline';
    }
  };

  const getMealTypeColor = (mealType) => {
    switch (mealType) {
      case 'breakfast': return '#FACC15';
      case 'lunch': return '#3B82F6';
      case 'dinner': return '#3B82F6';
      case 'snack': return '#AF52DE';
      default: return '#666';
    }
  };

  const openGoogleSheets = async () => {
    try {
      if (!googleSheetsUrl) {
        Alert.alert('No Google Sheet Available', 'Your personal Google Sheet will be created when your plan is generated or updated.');
        return;
      }

      const supported = await Linking.canOpenURL(googleSheetsUrl);
      if (supported) {
        await Linking.openURL(googleSheetsUrl);
      } else {
        Alert.alert('Error', 'Cannot open Google Sheets. Please check the URL.');
      }
    } catch (error) {
      console.error('Error opening Google Sheets:', error);
      Alert.alert('Error', 'Failed to open Google Sheets');
    }
  };

  const requestPlanChange = () => {
    Alert.alert(
      'Request Plan Change',
      'Would you like to request changes to your workout or diet plan? This will send an email to our admin team.',
      [
        {
          text: 'Cancel',
          style: 'cancel'
        },
        {
          text: 'Send Email',
          onPress: () => {
            const subject = 'Plan Change Request';
            const body = `Hi Admin Team,

I would like to request changes to my current plan. Please review my progress and suggest modifications.

Current Plan Version: ${currentPlan?.versionNumber || 'Unknown'}
Plan Type: ${currentPlan?.generatedBy || 'Unknown'}

Please let me know what changes you recommend.

Thank you!`;

            const emailUrl = `mailto:sweatandsnack2024@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

            Linking.openURL(emailUrl).catch(() => {
              Alert.alert('Error', 'Could not open email client. Please email sweatandsnack2024@gmail.com directly.');
            });
          }
        }
      ]
    );
  };

  const renderMeal = (meal, mealIndex) => (
    <View key={mealIndex} style={styles.mealCard}>
      <View style={styles.mealHeader}>
        <View style={styles.mealTypeContainer}>
          <Ionicons
            name={getMealTypeIcon(meal.mealType)}
            size={20}
            color={getMealTypeColor(meal.mealType)}
          />
          <Text style={[styles.mealType, { color: getMealTypeColor(meal.mealType) }]}>
            {meal.mealType.charAt(0).toUpperCase() + meal.mealType.slice(1)}
          </Text>
        </View>
        <View style={styles.servingSizeContainer}>
          <Ionicons name="restaurant" size={14} color="#666" />
          <Text style={styles.servingSize}>{meal.servingSize}</Text>
        </View>
      </View>

      <Text style={styles.mealName}>{meal.name}</Text>

      <View style={styles.macrosContainer}>
        <View style={styles.macroItem}>
          <Ionicons name="flame" size={16} color="#FACC15" />
          <Text style={styles.macroValue}>{meal.macros.calories}</Text>
          <Text style={styles.macroLabel}>Cal</Text>
        </View>
        <View style={styles.macroItem}>
          <Ionicons name="fitness" size={16} color="#10B981" />
          <Text style={styles.macroValue}>{meal.macros.protein}g</Text>
          <Text style={styles.macroLabel}>Protein</Text>
        </View>
        <View style={styles.macroItem}>
          <Ionicons name="leaf" size={16} color="#45B7D1" />
          <Text style={styles.macroValue}>{meal.macros.carbs}g</Text>
          <Text style={styles.macroLabel}>Carbs</Text>
        </View>
        <View style={styles.macroItem}>
          <Ionicons name="water" size={16} color="#96CEB4" />
          <Text style={styles.macroValue}>{meal.macros.fats}g</Text>
          <Text style={styles.macroLabel}>Fats</Text>
        </View>
      </View>

      <View style={styles.ingredientsContainer}>
        <View style={styles.sectionHeader}>
          <Ionicons name="list" size={16} color="#333" />
          <Text style={styles.ingredientsTitle}>Ingredients</Text>
        </View>
        <View style={styles.ingredientsList}>
          {meal.ingredients.map((ingredient, index) => (
            <View key={index} style={styles.ingredientItem}>
              <Text style={styles.ingredientBullet}></Text>
              <Text style={styles.ingredientsText}>{ingredient}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.preparationContainer}>
        <View style={styles.sectionHeader}>
          <Ionicons name="construct" size={16} color="#333" />
          <Text style={styles.preparationTitle}>Instructions</Text>
        </View>
        <Text style={styles.preparationText}>{meal.preparation}</Text>
      </View>
    </View>
  );

  const renderDaySelector = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const currentDayIndex = getCurrentDayIndex();

    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.daySelector}
        contentContainerStyle={styles.daySelectorContent}
      >
        {days.map((day, index) => (
          <TouchableOpacity
            key={index}
            style={[
              styles.dayButton,
              selectedDay === index && styles.dayButtonActive,
              index === currentDayIndex && styles.dayButtonToday
            ]}
            onPress={() => setSelectedDay(index)}
          >
            <Text style={[
              styles.dayButtonText,
              selectedDay === index && styles.dayButtonTextActive,
              index === currentDayIndex && styles.dayButtonTextToday
            ]}>
              {day}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea} edges={['top', 'left', 'right']}>
        <View style={styles.loadingContainer}>
          <Text style={{ color: '#FFFFFF' }}>Loading diet plan...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!currentPlan || !currentPlan.dietPlan) {
    return (
      <SafeAreaView style={styles.safeArea} edges={['left', 'right']}>
        <View style={styles.noPlanContainer}>
          <Ionicons name="restaurant-outline" size={48} color="#ccc" />
          <Text style={styles.noPlanText}>No diet plan available</Text>
          <Text style={styles.noPlanSubtext}>Contact your trainer or generate a new plan</Text>
        </View>
      </SafeAreaView>
    );
  }

  // Find the correct day plan by matching day name instead of using index
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const selectedDayName = days[selectedDay];
  const selectedDayPlan = currentPlan.dietPlan.find(plan => plan.day === selectedDayName);

  const dailyMacros = selectedDayPlan?.meals.reduce((totals, meal) => ({
    calories: totals.calories + meal.macros.calories,
    protein: totals.protein + meal.macros.protein,
    carbs: totals.carbs + meal.macros.carbs,
    fats: totals.fats + meal.macros.fats,
  }), { calories: 0, protein: 0, carbs: 0, fats: 0 });

  return (
    <View style={[styles.safeArea, { paddingTop: insets.top }]}>
      <ScrollView
        style={styles.container}
        contentContainerStyle={[styles.scrollContent]}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        {/* <View style={[styles.header, { paddingTop: Platform.OS === 'web' ? (insets.top > 0 ? insets.top : 16) : Math.max(insets.top + 8, 16) }]}> */}
        <View style={[styles.header, { paddingTop: 0 }]}>
          <Text style={styles.title}>Diet Plan</Text>
          <Text style={styles.subtitle}>{selectedDayPlan?.day || 'Select a day'}</Text>
        </View>

        {/* Day Selector */}
        {renderDaySelector()}

        {/* Target Macros */}
        {currentPlan?.macroTargets && (
          <View style={[styles.targetMacrosContainer, styles.firstCard]}>
            <Text style={styles.targetMacrosTitle}>Daily Target Macros</Text>
            <View style={styles.targetMacrosGrid}>
              <View style={styles.targetMacrosItem}>
                <Text style={styles.targetMacrosValue}>{currentPlan.macroTargets.calories}</Text>
                <Text style={styles.targetMacrosLabel}>Calories</Text>
              </View>
              <View style={styles.targetMacrosItem}>
                <Text style={styles.targetMacrosValue}>{currentPlan.macroTargets.protein}g</Text>
                <Text style={styles.targetMacrosLabel}>Protein</Text>
              </View>
              <View style={styles.targetMacrosItem}>
                <Text style={styles.targetMacrosValue}>{currentPlan.macroTargets.carbs}g</Text>
                <Text style={styles.targetMacrosLabel}>Carbs</Text>
              </View>
              <View style={styles.targetMacrosItem}>
                <Text style={styles.targetMacrosValue}>{currentPlan.macroTargets.fats}g</Text>
                <Text style={styles.targetMacrosLabel}>Fats</Text>
              </View>
            </View>
          </View>
        )}

        {/* Flexible Diet Message */}
        <View style={styles.flexibleDietContainer}>
          <View style={styles.flexibleDietHeader}>
            <Ionicons name="information-circle-outline" size={20} color="#3B82F6" />
            <Text style={styles.flexibleDietTitle}>Diet Flexibility</Text>
          </View>
          <Text style={styles.flexibleDietText}>
            While we provide specific meal suggestions, feel free to substitute with whole foods that fit within your calorie range.
            Focus on hitting your macro targets while choosing foods you enjoy and that work with your lifestyle.
          </Text>
        </View>

        {/* Daily Macros Summary */}
        {dailyMacros && (
          <View style={styles.macrosSummary}>
            <Text style={styles.macrosSummaryTitle}>Today's Macros</Text>
            <View style={styles.macrosSummaryGrid}>
              <View style={styles.macrosSummaryItem}>
                <Text style={styles.macrosSummaryValue}>{dailyMacros.calories}</Text>
                <Text style={styles.macrosSummaryLabel}>Calories</Text>
              </View>
              <View style={styles.macrosSummaryItem}>
                <Text style={styles.macrosSummaryValue}>{dailyMacros.protein}g</Text>
                <Text style={styles.macrosSummaryLabel}>Protein</Text>
              </View>
              <View style={styles.macrosSummaryItem}>
                <Text style={styles.macrosSummaryValue}>{dailyMacros.carbs}g</Text>
                <Text style={styles.macrosSummaryLabel}>Carbs</Text>
              </View>
              <View style={styles.macrosSummaryItem}>
                <Text style={styles.macrosSummaryValue}>{dailyMacros.fats}g</Text>
                <Text style={styles.macrosSummaryLabel}>Fats</Text>
              </View>
            </View>
          </View>
        )}

        {/* Meals */}
        <View style={styles.mealsContainer}>
          {selectedDayPlan?.meals ? (
            selectedDayPlan.meals.map((meal, index) => renderMeal(meal, index))
          ) : (
            <View style={styles.noMealsContainer}>
              <Ionicons name="restaurant-outline" size={48} color="#ccc" />
              <Text style={styles.noMealsText}>No meals planned for {selectedDayName}</Text>
              <Text style={styles.noMealsSubtext}>This day might be a rest day or meals haven't been planned yet</Text>
            </View>
          )}
        </View>


        {/* Plan Change Request */}
        <View style={styles.planChangeContainer}>
          <View style={styles.planChangeHeader}>
            <Ionicons name="mail-outline" size={24} color="#FACC15" />
            <Text style={styles.planChangeTitle}>Need Plan Changes?</Text>
          </View>
          <Text style={styles.planChangeDescription}>
            If you need modifications to your workout or diet plan, email us at{' '}
            <Text style={styles.planChangeEmail} onPress={requestPlanChange}>
              sweatandsnack2024@gmail.com
            </Text>
            {' '}and we'll help you out!
          </Text>
        </View>

        {/* Reason for Change - Only show if admin notes exist */}
        {latestAdminNotes && (
          <View style={styles.reasonContainer}>
            <View style={styles.reasonHeader}>
              <Ionicons name="information-circle" size={20} color="#3B82F6" />
              <Text style={styles.reasonTitle}>Why your plan was updated</Text>
            </View>
            <Text style={styles.reasonText}>{latestAdminNotes}</Text>
            {currentPlan && (
              <Text style={styles.reasonDate}>
                Version {currentPlan.versionNumber} {new Date(currentPlan.createdAt).toLocaleDateString('en-GB')}
              </Text>
            )}
          </View>
        )}

        {/* Initial Plan Message for Human Plan */}
        {currentPlan?.generatedBy === 'Human' && currentPlan?.versionNumber === 1 && (
          <View style={[styles.reasonContainer, { borderLeftColor: '#FACC15' }]}>
            <View style={styles.reasonHeader}>
              <Ionicons name="information-circle" size={20} color="#FACC15" />
              <Text style={[styles.reasonTitle, { color: '#FACC15' }]}>Initial Plan</Text>
            </View>
            <Text style={styles.reasonText}>
              This is your starting plan. Your personal trainer may make adjustments to this plan.
            </Text>
          </View>
        )}

        {/* Plan Info */}
        <View style={styles.planInfoContainer}>
          <Text style={styles.planInfoTitle}>Plan Information</Text>
          <View style={styles.planInfoRow}>
            <Text style={styles.planInfoLabel}>Version:</Text>
            <Text style={styles.planInfoValue}>{currentPlan.versionNumber}</Text>
          </View>
          <View style={styles.planInfoRow}>
            <Text style={styles.planInfoLabel}>Generated by:</Text>
            <Text style={styles.planInfoValue}>{currentPlan.generatedBy}</Text>
          </View>
          <View style={styles.planInfoRow}>
            <Text style={styles.planInfoLabel}>Created:</Text>
            <Text style={styles.planInfoValue}>
              {new Date(currentPlan.createdAt).toLocaleDateString('en-GB')}
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B0E14',
  },
  container: {
    flex: 1,
    backgroundColor: '#0B0E14',
  },
  scrollContent: {
    paddingBottom: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: SCREEN_HEIGHT * 0.7,
  },
  header: {
    backgroundColor: '#161C2D',
    paddingHorizontal: Math.max(16, SCREEN_WIDTH * 0.05),
    // paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#2C365E',
    shadowcolor: '#FFFFFF',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    minHeight: 80,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  subtitle: {
    fontSize: 16,
    color: '#6D759E',
    marginTop: 4,
  },
  daySelector: {
    backgroundColor: '#161C2D',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  daySelectorContent: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  dayButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 12,
    backgroundColor: '#0B0E14',
  },
  dayButtonActive: {
    backgroundColor: '#3B82F6',
  },
  dayButtonToday: {
    backgroundColor: '#3B82F6',
  },
  dayButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6D759E',
  },
  dayButtonTextActive: {
    color: 'white',
  },
  dayButtonTextToday: {
    color: 'white',
  },
  macrosSummary: {
    backgroundColor: '#161C2D',
    margin: 20,
    borderRadius: 16,
    padding: 16,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  macrosSummaryTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: 16,
    textAlign: 'center',
  },
  macrosSummaryGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  macrosSummaryItem: {
    alignItems: 'center',
  },
  macrosSummaryValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#3B82F6',
  },
  macrosSummaryLabel: {
    fontSize: 12,
    color: '#6D759E',
    marginTop: 4,
  },
  mealsContainer: {
    padding: 20,
    paddingTop: 0,
  },
  mealCard: {
    backgroundColor: '#161C2D',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  mealHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  mealTypeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  mealType: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  servingSizeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0B0E14',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  servingSize: {
    fontSize: 12,
    color: '#6D759E',
    marginLeft: 4,
  },
  mealName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: 12,
  },
  macrosContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: '#0B0E14',
    padding: 12,
    borderRadius: 12,
    marginBottom: 12,
  },
  macroItem: {
    alignItems: 'center',
    flex: 1,
  },
  macroValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginTop: 2,
  },
  macroLabel: {
    fontSize: 12,
    color: '#6D759E',
    marginTop: 2,
  },
  ingredientsContainer: {
    marginBottom: 12,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  ingredientsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 6,
  },
  ingredientsList: {
    marginLeft: 8,
  },
  ingredientItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  ingredientBullet: {
    fontSize: 14,
    color: '#6D759E',
    marginRight: 8,
    marginTop: 2,
  },
  ingredientsText: {
    fontSize: 14,
    color: '#6D759E',
    lineHeight: 20,
    flex: 1,
  },
  preparationContainer: {
    marginBottom: 0,
  },
  preparationTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 6,
  },
  preparationText: {
    fontSize: 14,
    color: '#6D759E',
    lineHeight: 20,
    marginLeft: 8,
  },
  noPlanContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  noPlanText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#6D759E',
    marginTop: 16,
  },
  noPlanSubtext: {
    fontSize: 14,
    color: '#6D759E',
    marginTop: 4,
    textAlign: 'center',
  },
  planInfoContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    marginTop: 0,
    borderRadius: 16,
    padding: 16,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  planInfoTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: 12,
  },
  planInfoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  planInfoLabel: {
    fontSize: 14,
    color: '#6D759E',
  },
  planInfoValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
  },
  // Target Macros Styles
  firstCard: {
    marginTop: 16,
  },
  targetMacrosContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    marginTop: 0,
    borderRadius: 16,
    padding: 16,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    borderLeftWidth: 4,
    borderLeftColor: '#3B82F6',
  },
  targetMacrosTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: 16,
    textAlign: 'center',
  },
  targetMacrosGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  targetMacrosItem: {
    alignItems: 'center',
  },
  targetMacrosValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#3B82F6',
  },
  targetMacrosLabel: {
    fontSize: 12,
    color: '#6D759E',
    marginTop: 4,
  },
  // No Meals Styles
  noMealsContainer: {
    alignItems: 'center',
    padding: 40,
    backgroundColor: '#161C2D',
    borderRadius: 16,
    marginBottom: 16,
  },
  noMealsText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#6D759E',
    marginTop: 16,
  },
  noMealsSubtext: {
    fontSize: 14,
    color: '#6D759E',
    marginTop: 4,
    textAlign: 'center',
  },
  // Flexible Diet Styles
  flexibleDietContainer: {
    backgroundColor: '#161C2D',
    borderRadius: 12,
    padding: 16,
    margin: 20,
    marginTop: 10,
    borderLeftWidth: 4,
    borderLeftColor: '#3B82F6',
  },
  flexibleDietHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  flexibleDietTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#3B82F6',
    marginLeft: 8,
  },
  flexibleDietText: {
    fontSize: 14,
    color: '#6D759E',
    lineHeight: 20,
  },
  // Google Sheets Styles
  sheetsContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    marginTop: 0,
    borderRadius: 16,
    padding: 16,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sheetsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sheetsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  sheetsDescription: {
    fontSize: 14,
    color: '#6D759E',
    marginBottom: 16,
    lineHeight: 20,
  },
  sheetsButton: {
    backgroundColor: '#10B981',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  sheetsButtonDisabled: {
    backgroundColor: '#ccc',
    opacity: 0.6,
  },
  sheetsButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  sheetsNote: {
    fontSize: 12,
    color: '#6D759E',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 8,
  },
  // Plan Change Styles
  planChangeContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    marginTop: 0,
    borderRadius: 16,
    padding: 16,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  planChangeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  planChangeTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  planChangeDescription: {
    fontSize: 14,
    color: '#6D759E',
    lineHeight: 20,
  },
  planChangeEmail: {
    color: '#FACC15',
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  // Reason for Change Styles
  reasonContainer: {
    backgroundColor: '#161C2D',
    borderRadius: 12,
    padding: 16,
    margin: 20,
    marginTop: 10,
    borderLeftWidth: 4,
    borderLeftColor: '#3B82F6',
  },
  reasonHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  reasonTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#3B82F6',
    marginLeft: 8,
  },
  reasonText: {
    fontSize: 14,
    color: '#6D759E',
    lineHeight: 20,
    marginBottom: 8,
  },
  reasonDate: {
    fontSize: 12,
    color: '#6D759E',
    fontStyle: 'italic',
  },
});

export default DietPlanScreen;
